"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GuiLogger = void 0;
const ua_parser_js_1 = __importDefault(require("ua-parser-js"));
const const_1 = require("../../const");
const ApiBatchLogger_1 = require("../ApiBatchLogger/ApiBatchLogger");
const convertToDefinedValue_1 = require("./utils/convertToDefinedValue");
const getObjectWithProperties_1 = require("./utils/getObjectWithProperties");
/**
 * GuiLogger with accurate typing.
 * Provides structured logs and the option to enable debug mode for console logging.
 * Stores information about the current route and user device data and includes it in logs sent to the server, as required by the server API.
 */
class GuiLogger {
    apiBatchLogger;
    appName;
    currentRouteData;
    debug;
    environment;
    userAgentData;
    user;
    onLogEvent;
    onWarning;
    init({ appName, debug = false, dryRun = true, environment, host, localStorageKey, basicAuthUsername, basicAuthPassword, onLogEvent, onWarning, onException, }) {
        this.apiBatchLogger = new ApiBatchLogger_1.ApiBatchLogger({
            host,
            localStorageKey,
            dryRun,
            basicAuthUsername,
            basicAuthPassword,
            onException,
        });
        this.appName = appName;
        this.debug = debug;
        this.environment = environment;
        this.userAgentData = this.getUserAgentData();
        this.user = null;
        this.onLogEvent = onLogEvent;
        this.onWarning = onWarning;
    }
    navigation(pageLocation, extraData) {
        this.currentRouteData = { ...pageLocation };
        this.executeLog(const_1.RESERVED_EVENTS.navigation, extraData);
    }
    modal(modalName, extraData) {
        this.executeLog(const_1.RESERVED_EVENTS.modal, { ...(extraData ?? {}), modalName });
    }
    helpModal(helpId, extraData) {
        this.executeLog(const_1.RESERVED_EVENTS.modal, {
            ...(extraData ?? {}),
            ...{
                modalName: "help",
                helpId,
            },
        });
    }
    changeFilters(eventName, filtersBarData, extraData) {
        this.executeLog(eventName, {
            ...(extraData ?? {}),
            filterBar: filtersBarData,
        });
    }
    showTooltip(tooltipName, extraData) {
        this.executeLog(const_1.RESERVED_EVENTS.showTooltip, { ...(extraData ?? {}), tooltipName });
    }
    columnResize(columnKey, columnWidth, extraData) {
        this.executeLog(const_1.RESERVED_EVENTS.columnResize, { ...(extraData ?? {}), columnKey, columnWidth });
    }
    columnDataSort(columnKey, direction, extraData) {
        this.executeLog(const_1.RESERVED_EVENTS.columnDataSort, { ...(extraData ?? {}), columnKey, direction });
    }
    externalLinkClick(linkName, url, extraData) {
        this.executeLog(const_1.RESERVED_EVENTS.externalLinkClick, { ...(extraData ?? {}), linkName, url });
    }
    generateExcel(excelName, extraData) {
        this.executeLog(const_1.RESERVED_EVENTS.generateExcel, { ...(extraData ?? {}), excelName });
    }
    copyToClipboard(copiedTextType, extraData) {
        this.executeLog(const_1.RESERVED_EVENTS.copyToClipboard, { ...(extraData ?? {}), copiedTextType });
    }
    log(eventName, extraData) {
        this.executeLog(eventName, extraData);
    }
    setUser(user) {
        if (this.user) {
            const isDifferentUser = this.user.email !== user.email || this.user.login !== user.login;
            if (isDifferentUser)
                this.printWarning(const_1.WARNINGS.user.title, const_1.WARNINGS.user.message);
            return;
        }
        this.user = user;
        this.printToConsole(user, const_1.CONSOLE_MESSAGES.user.title);
    }
    clearUser() {
        this.user = null;
        this.printToConsole(this.user, const_1.CONSOLE_MESSAGES.clearUser.title);
    }
    flushLogs() {
        if (!this.apiBatchLogger) {
            const { title, message } = const_1.WARNINGS.notInitialized;
            this.printWarning(title, message, const_1.RESERVED_EVENTS.flushLogs);
            return Promise.resolve();
        }
        this.printToConsole(null, const_1.CONSOLE_MESSAGES.flushLogs.title);
        return this.apiBatchLogger.sendRecordsIfAny();
    }
    destroy() {
        this.currentRouteData = undefined;
        this.apiBatchLogger = undefined;
    }
    getUserAgentData() {
        const { ua, browser, device, os } = new ua_parser_js_1.default().getResult();
        return {
            ua,
            browser: {
                name: (0, convertToDefinedValue_1.convertToDefinedValue)(browser.name),
                version: (0, convertToDefinedValue_1.convertToDefinedValue)(browser.version),
            },
            device: {
                model: (0, convertToDefinedValue_1.convertToDefinedValue)(device.model),
                type: (0, convertToDefinedValue_1.convertToDefinedValue)(device.type),
                vendor: (0, convertToDefinedValue_1.convertToDefinedValue)(device.vendor),
            },
            os: {
                name: (0, convertToDefinedValue_1.convertToDefinedValue)(os.name),
                version: (0, convertToDefinedValue_1.convertToDefinedValue)(os.version),
            },
        };
    }
    executeLog(event, extraData) {
        if (!this.apiBatchLogger || !this.appName || !this.environment || !this.userAgentData) {
            const { title, message } = const_1.WARNINGS.notInitialized;
            this.printWarning(title, message, event);
            return;
        }
        if (!this.currentRouteData) {
            const { title, message } = const_1.WARNINGS.noNavigationContext;
            this.printWarning(title, message, event);
            return;
        }
        const definedRecordValues = {
            app: this.appName,
            event,
            environment: this.environment,
            routeData: this.currentRouteData,
            userAgent: this.userAgentData,
            user: this.user ?? null,
            extraData: extraData || {},
        };
        const record = this.getLogRecordData(definedRecordValues);
        this.apiBatchLogger.report(record);
        this.printToConsole(record);
        this.onLogEvent?.(event, record);
        return record;
    }
    printWarning(warningTitle, warningMessage, event) {
        window.console.warn(`%c[GUI Logger] - ${warningTitle}. ${warningMessage}`, const_1.logTitleStyles.warn);
        if (event)
            this.onWarning?.(event, warningTitle, warningMessage);
    }
    printToConsole(data, title, message) {
        const consoleMessage = `%c[GUI Logger]${title ? ` - ${title}` : ""}${message ? ". " + message : ""}`;
        if (this.debug) {
            window.console.log(consoleMessage, const_1.logTitleStyles.log, ...(data ? [data] : []));
        }
    }
    getLogRecordData({ app, event, environment, routeData, userAgent, user, extraData, }) {
        return {
            app,
            event,
            environment,
            eventTime: new Date().toISOString(),
            location: this.getLocationData(routeData),
            userAgent,
            viewport: this.getViewportDimensions(),
            extra: (0, getObjectWithProperties_1.getObjectWithProperties)(extraData),
            ...(user && { user }),
        };
    }
    getLocationData(routeData) {
        const { href, hash, host, origin, pathname, port, protocol, search } = window.location;
        const { pageName, pageParams } = routeData;
        const queryParams = new URLSearchParams(window.location.search);
        const flatQueryParams = Object.fromEntries(Array.from(queryParams.entries()));
        return {
            href,
            extra: {
                hash,
                host,
                origin,
                pathname,
                port,
                protocol,
                search,
            },
            queryParams: (0, getObjectWithProperties_1.getObjectWithProperties)(flatQueryParams),
            pageParams: (0, getObjectWithProperties_1.getObjectWithProperties)(pageParams),
            pageName,
        };
    }
    getViewportDimensions() {
        return {
            width: window.innerWidth,
            height: window.innerHeight,
        };
    }
}
exports.GuiLogger = GuiLogger;
//# sourceMappingURL=GuiLogger.js.map